import { observer } from "mobx-react-lite";
import { Check } from "lucide-react";

import { useTran } from "@/hooks";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { InputField } from "@/components/common";
import { useSurveyShareStore } from "../hooks";

const SurveyShareInternalTab = observer(({ onSendInvites }) => {
  const { t } = useTran();
  const shareStore = useSurveyShareStore();

  return (
    <>
      <div className="flex-1 rounded-lg border border-slate-200 bg-slate-50/70 p-4 dark:border-slate-700 dark:bg-slate-900/40 flex flex-col min-h-0">
        <p className="text-sm font-semibold text-foreground">
          {t("Search internal users")}
        </p>
        <p className="text-xs text-muted-foreground">
          {t(
            "Select one or more existing internal users. Only selected users will be able to take this survey.",
          )}
        </p>
        <InputField
          value={shareStore.internalSearch}
          onChange={(event) => shareStore.searchInternal(event.target.value)}
          placeholder={t("Search by name or email...")}
          className="mt-3"
        />

        <div className="mt-4 flex-1 space-y-3 overflow-y-auto">
          {shareStore.internalSuggestions.length === 0 &&
          !shareStore.suggestionsLoading ? (
            <div className="rounded-radius border border-dashed border-border bg-background p-4 text-center text-xs text-muted-foreground">
              {t("No matching users found.")}
            </div>
          ) : null}
          {shareStore.internalSuggestions.map((user) => {
            const selected = shareStore.selectedInternal.has(user.email);
            return (
              <div
                key={user.email}
                className="flex items-center justify-between rounded-radius border border-border bg-card p-3 text-xs shadow-sm"
              >
                <div>
                  <p className="font-medium text-foreground">
                    {user.name || user.email}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    {user.email}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant={selected ? "default" : "outline"}
                  className="rounded-full"
                  onClick={() => shareStore.toggleInternal(user.email)}
                >
                  {selected ? <Check className="h-4 w-4" /> : t("Select")}
                </Button>
              </div>
            );
          })}
          {shareStore.internalHasMore ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={shareStore.loadMoreInternal}
              disabled={shareStore.suggestionsLoading}
            >
              {shareStore.suggestionsLoading ? t("Loading...") : t("Load more")}
            </Button>
          ) : null}
        </div>

        {shareStore.internalError ? (
          <p className="mt-3 text-xs text-destructive">
            {shareStore.internalError}
          </p>
        ) : null}
      </div>

      <div className="mt-4 flex items-center justify-between">
        <Badge>
          {shareStore.selectedInternalCount} {t("selected")}
        </Badge>
        <Button
          onClick={onSendInvites}
          disabled={
            shareStore.inviteLoading || shareStore.selectedInternalCount === 0
          }
        >
          {shareStore.inviteLoading ? t("Sending...") : t("Send Invites")}
        </Button>
      </div>
    </>
  );
});

export default SurveyShareInternalTab;
